/**
 * Filter configuration for the search functionality.
 * Defines all available filters and their options for filtering games.
 *
 * @remarks
 * Filter ID to API Response Field Mapping:
 * - `lines`: Maps to "lines" field in API response
 * - `features`: Maps to game features in API response
 * - `max_win`: Maps to "maxAwardingCoef" field in API response
 * - `payout`: Maps to "paymode" field in API response
 * - `jackpot`: Maps to "jackpotName" or "displayJackpotName" (if present) field in API response
 */

import type { IFilterConfig } from '../types/filter.types';

/**
 * Filter categories that only allow single selection.
 * When a user selects an option in these categories, any previous selection is deselected.
 */
export const SINGLE_SELECT_FILTERS: string[] = [
  'lines',
  // 'features',
  'max_win',
  'payout',
  'jackpot',
  // 'campaigns',
];

export const FILTERS: IFilterConfig[] = [
  {
    id: 'lines',
    labelKey: 'search.filter_lines',
    defaultLabel: 'Lines',
    options: [],
  },
  {
    id: 'features',
    labelKey: 'search.filter_features',
    defaultLabel: 'Features',
    options: [
      { id: 'buy_bonus', labelKey: 'sections.names.BUY_BONUS', label: 'Buy Bonus' },
      { id: 'expanding_wilds', labelKey: 'search.filter_options.features.expanding_wild', label: 'Expanding Wild' },
      { id: 'free_spins', labelKey: 'search.filter_options.features.free_spins', label: 'Free Spins' },
    ],
  },
  {
    id: 'max_win',
    labelKey: 'search.filter_max_win',
    defaultLabel: 'Max Win',
    options: [
      { id: 'lte_1200', label: '≤ x1 200' },
      { id: '1200_3000', label: 'x1 200 – x3 000' },
      { id: '3000_8000', label: 'x3 000 – x8 000' },
      { id: 'gt_8000', label: '> x8 000' },
    ],
  },
  {
    id: 'payout',
    labelKey: 'search.filter_payout',
    defaultLabel: 'Payout',
    options: [
      { id: 'cluster_pay', labelKey: 'search.filter_options.payout.cluster_pay', label: 'Cluster Pay' },
      { id: 'fixed_lines', labelKey: 'search.filter_options.payout.fixed_lines', label: 'Lines' },
      { id: 'jumboways', label: 'Jumboways' },
      { id: 'scatter_pay', labelKey: 'search.filter_options.payout.scatter_pay', label: 'Scatter Pay' },
      { id: 'ways_to_pay', label: 'Ways to Pay' },
    ],
  },
  {
    id: 'jackpot',
    labelKey: 'search.filter_jackpot',
    defaultLabel: 'Jackpot',
    options: [
      { id: 'bell_link', label: 'Bell Link' },
      { id: 'bell_link_boost', label: 'Bell Link Boost' },
      { id: 'vip_bell_link', label: 'VIP Bell Link' },
      { id: 'vip_bell_link_boost', label: 'VIP Bell Link Boost' },
      { id: 'clover_chance', label: 'Clover Chance' },
      { id: 'gods_&_kings_link', label: 'Gods & Kings Link' },
      { id: 'vip_gods_&_kings_link', label: 'VIP Gods & Kings Link' },
      { id: 'high_cash', label: 'High Cash' },
      { id: 'vip_high_cash', label: 'VIP High Cash' },
      { id: 'instantlink', label: 'Instant Link' },
      { id: 'premium_link', label: 'Premium Link' },
      { id: 'progressive', labelKey: 'search.filter_options.jackpot.progressive', label: 'Progressive' },
      { id: 'high_rtp', label: 'High RTP' },
    ],
  },
  {
    id: 'campaigns',
    labelKey: 'search.filter_campaigns',
    defaultLabel: 'Campaigns',
    options: [
      { id: 'tournaments', labelKey: 'sections.names.TOURNAMENTS', label: 'Tournaments' },
      { id: 'prize_pop', label: 'Prize Pop' },
      { id: 'rebate', label: 'Rebate' },
    ],
  },
];
